import {
  el,
  fmtInt,
  fmtDate,
  fmtDay,
  fmtMoneyCents,
  fmtPct,
  fmtCompact,
  relativeTime,
} from '../ui/dom';
import { renderShell, renderError } from '../ui/shell';
import {
  kpiCard,
  kpiGrid,
  panel,
  panelGrid,
  segmented,
  caption,
} from '../ui/components';
import {
  lineAreaChart,
  barList,
  stackedBar,
  funnelChart,
  type Segment,
  type BarRow,
} from '../ui/charts';
import {
  getPlatformOverview,
  getPlatformGrowth,
  getMrrHistory,
  type PlatformOverview,
  type RealIdBreakdown,
  type GrowthMetric,
  type GrowthInterval,
} from '../api/platform';
import { listVenueRequests } from '../api/venue-requests';
import { UnauthorizedError } from '../api/client';
import { navigate } from '../router';
import type { RouteContext } from '../router';

const TIER_COLORS: Record<string, string> = {
  FREE: '#8e8e93',
  PRO: '#0071e3',
  BUSINESS: '#5e5ce6',
  ENTERPRISE: '#34c759',
};
const STATUS_COLORS: Record<string, string> = {
  ACTIVE: '#34c759',
  PAST_DUE: '#ff9f0a',
  CANCELED: '#ff375f',
  SUSPENDED: '#ff375f',
};
const FALLBACK_COLORS = ['#0071e3', '#5e5ce6', '#34c759', '#ff9f0a', '#ff375f', '#8e8e93'];

function realIdSegments(r: RealIdBreakdown): Segment[] {
  return [
    { label: 'Compliant', value: r.compliant, color: 'var(--ok-fg)' },
    { label: 'Not compliant', value: r.nonCompliant, color: 'var(--danger-fg)' },
    { label: 'Not encoded', value: r.notEncoded, color: '#8e8e93' },
    { label: 'Not reported', value: r.notReported, color: 'var(--warn-fg)' },
  ];
}

function loadingBox(): HTMLElement {
  return el('div', { class: 'loading' }, ['Loading…']);
}

function errorFor(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong.';
}

// MrrHistoryPoint.mrr/arr are plain dollars (unlike PlatformOverview.revenue,
// which is cents) — the daily-snapshot table has no Cents suffix on its columns.
function money(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

function toSegments(rows: { label: string; count: number }[], colors: Record<string, string>): Segment[] {
  return rows.map((r, i) => ({
    label: r.label,
    value: r.count,
    color: colors[r.label] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length],
  }));
}

export async function renderOverview({ root }: RouteContext): Promise<void> {
  const content = renderShell(root, 'overview');

  let windowDays = 30;
  const cap = caption('Loading platform metrics…');
  const windowSegHost = el('div', { class: 'seg-host' });
  const head = el('div', { class: 'view-head' }, [
    el('div', { class: 'page-head' }, [el('h1', {}, ['Platform overview'])]),
    windowSegHost,
  ]);
  const kpiHost = el('div', {}, [loadingBox()]);
  const panelsHost = el('div');
  const venueRequestsHost = venueRequestsSection();
  const mrrHistoryHost = mrrHistorySection();
  const growthHost = growthSection();

  content.append(head, cap, kpiHost, venueRequestsHost, mrrHistoryHost, growthHost, panelsHost);

  function renderWindowSeg(): void {
    windowSegHost.replaceChildren(
      segmented(
        [
          { label: '7 days', value: '7' },
          { label: '30 days', value: '30' },
          { label: '90 days', value: '90' },
        ],
        String(windowDays),
        (v) => {
          windowDays = Number(v);
          renderWindowSeg();
          void loadOverview();
        },
      ),
    );
  }

  async function loadOverview(): Promise<void> {
    kpiHost.replaceChildren(loadingBox());
    panelsHost.replaceChildren();
    try {
      const o = await getPlatformOverview(windowDays);
      cap.textContent = `As of ${fmtDate(o.generatedAt)} · activity over the last ${o.windowDays} days`;
      kpiHost.replaceChildren(renderKpis(o));
      panelsHost.replaceChildren(renderPanels(o));
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(kpiHost, errorFor(err));
    }
  }

  renderWindowSeg();
  await loadOverview();
}

function renderKpis(o: PlatformOverview): HTMLElement {
  const rev = o.revenue;
  const revSub =
    rev.basis === 'unavailable'
      ? 'No active subscriptions yet'
      : `${fmtMoneyCents(rev.arrCents)} ARR${rev.basis === 'partial' ? ' · partial' : ''}`;
  const rejectionTone =
    o.scans.rejectionRate >= 0.3 ? 'danger' : o.scans.rejectionRate >= 0.15 ? 'warn' : 'default';

  return kpiGrid([
    kpiCard('Teams', fmtInt(o.teams.total), {
      sub: `+${fmtInt(o.teams.newInWindow)} new · ${fmtInt(o.teams.suspended)} suspended`,
    }),
    kpiCard('Active subscriptions', fmtInt(o.subscriptions.active), {
      sub: `${fmtInt(o.subscriptions.trials)} on trial`,
      tone: o.subscriptions.active > 0 ? 'ok' : 'default',
    }),
    kpiCard('Estimated MRR', fmtMoneyCents(rev.mrrCents), {
      sub: revSub,
      tone: rev.mrrCents > 0 ? 'ok' : 'default',
      title: 'Monitoring estimate across Stripe + Apple rails — not a billing source of record',
    }),
    kpiCard('Scans', fmtInt(o.scans.window), {
      sub: `${fmtInt(o.scans.today)} today · ${fmtCompact(o.scans.allTime)} all-time`,
    }),
    kpiCard('Rejection rate', fmtPct(o.scans.rejectionRate), {
      sub: `${fmtInt(o.scans.passbackAlerts)} passback alerts`,
      tone: rejectionTone,
    }),
    kpiCard('Venues', fmtInt(o.venues.total), {
      sub: `${fmtInt(o.venues.active)} active · +${fmtInt(o.venues.newInWindow)} new`,
    }),
    kpiCard('Team members', fmtInt(o.members.total), {
      sub: `${fmtInt(o.members.pendingInvites)} invites pending`,
    }),
    kpiCard('Door devices', fmtInt(o.devices.active), {
      sub: `${fmtInt(o.devices.seenLast24h)} seen in 24h · ${fmtInt(o.devices.revoked)} revoked`,
    }),
  ]);
}

function renderPanels(o: PlatformOverview): HTMLElement {
  const scanSegments: Segment[] = [
    { label: 'Accepted', value: o.scans.accepted, color: 'var(--ok-fg)' },
    { label: 'Rejected', value: o.scans.rejected, color: 'var(--danger-fg)' },
    { label: 'Undetermined', value: o.scans.undetermined, color: '#8e8e93' },
  ];

  const productRows: BarRow[] = o.subscriptions.byProduct.map((p) => ({
    label: prettyProduct(p.label),
    value: p.count,
  }));

  const topTeamRows: BarRow[] = o.topTeams.map((t) => ({
    label: t.name,
    value: t.scans,
  }));

  const funnelSteps = [
    { label: 'Signed up', value: o.funnel.signedUp },
    { label: 'Subscribed', value: o.funnel.subscribed },
    { label: 'Activated (first scan)', value: o.funnel.activated },
  ];

  return el('div', { class: 'stack-gap' }, [
    healthStrip(o),
    panelGrid([
      panel('Scans by result', [
        stackedBar(scanSegments),
        o.scans.topRejectionReasons.length
          ? el('div', { class: 'panel-subhead' }, ['Top rejection reasons'])
          : null,
        o.scans.topRejectionReasons.length
          ? barList(
              o.scans.topRejectionReasons.map((r) => ({
                label: prettyReason(r.label),
                value: r.count,
                color: 'var(--danger-fg)',
              })),
            )
          : null,
      ]),
      o.scans.realId
        ? panel('Scans by REAL ID indicator', [stackedBar(realIdSegments(o.scans.realId))], {
            subtitle: 'as encoded on the document',
          })
        : null,
      panel('Teams by tier', [
        o.teams.byTier.length
          ? barList(
              o.teams.byTier.map((t, i) => ({
                label: t.label,
                value: t.count,
                color: TIER_COLORS[t.label] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length],
              })),
            )
          : emptyNote('No teams yet'),
      ]),
      panel('Subscription status', [
        o.teams.byStatus.length
          ? stackedBar(toSegments(o.teams.byStatus, STATUS_COLORS))
          : emptyNote('No subscriptions yet'),
      ]),
      panel('Subscriptions by product', [
        productRows.length ? barList(productRows) : emptyNote('No linked subscriptions yet'),
        el('div', { class: 'panel-note' }, [
          `${fmtInt(o.subscriptions.appleActive)} Apple · ${fmtInt(o.subscriptions.stripeActive)} Stripe active`,
        ]),
      ]),
      panel('Top teams by scans', [
        topTeamRows.length ? barList(topTeamRows) : emptyNote('No scan activity in this window'),
      ]),
      panel('Activation (all-time)', [funnelChart(funnelSteps)], {
        subtitle: 'Signed up → subscribed → activated · all organizations, no time window',
      }),
      panel('Members by role', [
        o.members.byRole.length
          ? barList(
              o.members.byRole.map((r) => ({ label: prettyRole(r.label), value: r.count })),
            )
          : emptyNote('No members yet'),
      ]),
    ]),
  ]);
}

function healthStrip(o: PlatformOverview): HTMLElement {
  const h = o.health;
  const item = (label: string, value: string, tone: 'ok' | 'warn' | 'default') =>
    el('div', { class: `health-item health-${tone}` }, [
      el('span', { class: 'health-dot' }),
      el('span', { class: 'health-label' }, [label]),
      el('span', { class: 'health-value' }, [value]),
    ]);
  return el('section', { class: 'health-strip' }, [
    el('span', { class: 'health-title' }, ['System health']),
    item(
      'Stale sub statuses',
      fmtInt(h.staleSubscriptionStatuses),
      h.staleSubscriptionStatuses > 0 ? 'warn' : 'ok',
    ),
    item('Devices seen 24h', fmtInt(h.devicesSeenLast24h), 'default'),
    item('Errors (7d)', fmtInt(h.errorsLast7d), h.errorsLast7d > 0 ? 'warn' : 'ok'),
    item(
      'Last aggregation',
      h.lastConsumerAggregationAt ? relativeTime(h.lastConsumerAggregationAt) : 'never',
      h.lastConsumerAggregationAt ? 'ok' : 'warn',
    ),
  ]);
}

// ── Venue requests (self-managing; independent of the window selector) ───────
// Only shown when there's something to do — owners asking for a second venue
// under the 3.0 "single location by default" limit.
function venueRequestsSection(): HTMLElement {
  const body = el('div', {}, [loadingBox()]);

  async function load(): Promise<void> {
    try {
      const { requests } = await listVenueRequests('open');
      if (requests.length === 0) {
        body.replaceChildren(el('p', { class: 'panel-empty' }, ['No open requests.']));
        return;
      }
      body.replaceChildren(
        kpiGrid([kpiCard('Open', fmtInt(requests.length), { tone: 'warn' })]),
        el('div', { class: 'button-row', style: 'justify-content:flex-start' }, [
          el(
            'button',
            {
              class: 'btn btn-primary btn-sm',
              type: 'button',
              onClick: () => navigate('/venue-requests'),
            },
            ['Review requests'],
          ),
        ]),
      );
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      body.replaceChildren(el('div', { class: 'banner banner-error' }, [errorFor(err)]));
    }
  }

  void load();
  return panel('Venue requests', [body], {
    subtitle: 'Owners asking for an additional location',
  });
}

// ── MRR history (self-managing; daily snapshot table, fixed 90-day lookback) ─
function mrrHistorySection(): HTMLElement {
  const chartHost = el('div', { class: 'chart-host' }, [loadingBox()]);

  async function load(): Promise<void> {
    chartHost.replaceChildren(loadingBox());
    try {
      const { points } = await getMrrHistory(90);
      if (points.length === 0) {
        chartHost.replaceChildren(
          el('p', { class: 'panel-empty' }, [
            'History starts accruing from the first nightly snapshot.',
          ]),
        );
        return;
      }
      chartHost.replaceChildren(
        lineAreaChart(
          points.map((p) => ({ label: fmtDay(p.date), value: p.mrr })),
          { name: 'MRR', format: (n) => money(n), axisFormat: (n) => `$${fmtCompact(n)}` },
        ),
      );
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      chartHost.replaceChildren(
        el('div', { class: 'banner banner-error' }, [errorFor(err)]),
      );
    }
  }

  void load();
  return panel('MRR history', [chartHost], {
    subtitle: 'Nightly snapshot · last 90 days',
    wide: true,
  });
}

// ── Growth section (self-managing; independent of the window selector) ───────
function growthSection(): HTMLElement {
  let metric: GrowthMetric = 'teams';
  let interval: GrowthInterval = 'day';
  let rangeDays = 90;
  let mode: 'cumulative' | 'new' = 'cumulative';

  const controlsHost = el('div', { class: 'growth-controls' });
  const chartHost = el('div', { class: 'chart-host' }, [loadingBox()]);
  const summary = el('p', { class: 'panel-note' });

  function renderControls(): void {
    controlsHost.replaceChildren(
      segmented(
        [
          { label: 'Teams', value: 'teams' },
          { label: 'Subscriptions', value: 'subscriptions' },
          { label: 'Venues', value: 'venues' },
          { label: 'Members', value: 'members' },
          { label: 'Scans', value: 'scans' },
        ],
        metric,
        (v) => {
          metric = v as GrowthMetric;
          renderControls();
          void load();
        },
      ),
      el('div', { class: 'growth-controls-right' }, [
        segmented(
          [
            { label: 'Cumulative', value: 'cumulative' },
            { label: 'Net new', value: 'new' },
          ],
          mode,
          (v) => {
            mode = v as 'cumulative' | 'new';
            renderControls();
            void load();
          },
        ),
        segmented(
          [
            { label: 'Day', value: 'day' },
            { label: 'Week', value: 'week' },
            { label: 'Month', value: 'month' },
          ],
          interval,
          (v) => {
            interval = v as GrowthInterval;
            renderControls();
            void load();
          },
        ),
        segmented(
          [
            { label: '30d', value: '30' },
            { label: '90d', value: '90' },
            { label: '1y', value: '365' },
          ],
          String(rangeDays),
          (v) => {
            rangeDays = Number(v);
            renderControls();
            void load();
          },
        ),
      ]),
    );
  }

  async function load(): Promise<void> {
    chartHost.replaceChildren(loadingBox());
    try {
      const s = await getPlatformGrowth(metric, interval, rangeDays);
      const points = s.points.map((p) => ({
        label: fmtDay(p.period),
        value: mode === 'cumulative' ? p.cumulative : p.new,
      }));
      chartHost.replaceChildren(
        lineAreaChart(points, { name: `${metric} ${mode}`, format: (n) => fmtInt(n) }),
      );
      summary.textContent = `${fmtInt(s.totalNew)} new ${metric} over the selected range`;
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      chartHost.replaceChildren(
        el('div', { class: 'banner banner-error' }, [errorFor(err)]),
      );
    }
  }

  renderControls();
  void load();
  return panel('Growth', [controlsHost, chartHost, summary], { wide: true });
}

function emptyNote(text: string): HTMLElement {
  return el('p', { class: 'panel-empty' }, [text]);
}

function prettyProduct(id: string): string {
  return id
    .replace(/^com\.checkidapp\./, '')
    .replace(/[._]/g, ' ')
    .replace(/\bmonthly\b/i, 'Monthly')
    .replace(/\byearly\b/i, 'Yearly')
    .replace(/\bbusiness\b/i, 'Business')
    .trim();
}

function prettyReason(r: string): string {
  return r.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function prettyRole(r: string): string {
  return r.charAt(0) + r.slice(1).toLowerCase();
}
